import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'sonner'
import {
  Search, MapPin, ChevronRight, X,
  FileText, MessageSquare, Lock, Users, Euro
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { useAuth } from '../context/AuthContext'
import { messagingPaths } from '../utils/messagingPaths'
import PublicPageHeader from '../components/PublicPageHeader'
import PublicFooter from '../components/PublicFooter'
import './RequestsDirectory.css'

import API from '../constants/api'
// API base via getApiBase()

/**
 * Parses `salaryRange` (string) for budget display on REQUEST posts.
 * Accepts JSON `{"min":100,"max":500}` or a plain string.
 */
const parseBudget = (salaryRange) => {
  if (!salaryRange) return null
  try {
    const parsed = JSON.parse(salaryRange)
    if (typeof parsed === 'object' && parsed !== null) return parsed
  } catch {
    // Not JSON — treat as plain string label
    return { label: salaryRange }
  }
  return null
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diff === 0) return "Aujourd'hui"
  if (diff === 1) return 'Hier'
  if (diff < 7) return `Il y a ${diff} jours`
  return date.toLocaleDateString('fr-FR')
}

export default function RequestsDirectory() {
  const { user, loading: authLoading, isAuthenticated, getAuthToken, isProView } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [requests, setRequests] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [accessDenied, setAccessDenied] = useState(false)

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    categoryId: searchParams.get('categoryId') || '',
    ville: searchParams.get('ville') || '',
  })

  const fetchData = useCallback(async () => {
    if (authLoading) return

    setLoading(true)
    setAccessDenied(false)

    try {
      const authToken = getAuthToken()
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {}

      const apiParams = { type: 'REQUEST', status: 'PUBLISHED' }
      if (filters.search) apiParams.q = filters.search
      if (filters.categoryId && filters.categoryId !== 'all') apiParams.categoryId = filters.categoryId
      if (filters.ville) apiParams.city = filters.ville

      const [requestsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/posts`, { params: apiParams, headers }),
        axios.get(`${API}/categories`),
      ])

      const requestsData = requestsRes.data?.data ?? []
      const meta = requestsRes.data?.meta
      setRequests(Array.isArray(requestsData) ? requestsData : [])
      setTotalCount(meta?.total ?? (Array.isArray(requestsData) ? requestsData.length : 0))

      const categoriesRaw = categoriesRes.data?.data ?? categoriesRes.data?.categories ?? categoriesRes.data ?? []
      setCategories(Array.isArray(categoriesRaw) ? categoriesRaw : [])
    } catch (error) {
      const status = error.response?.status
      if (status === 403 || status === 401) {
        setAccessDenied(true)
      } else {
        console.error('[RequestsDirectory] fetch error:', error)
        toast.error('Erreur lors du chargement des demandes')
      }
    } finally {
      setLoading(false)
    }
  }, [filters, authLoading, getAuthToken])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearch = (e) => {
    e.preventDefault()
    const newParams = new URLSearchParams()
    if (filters.search) newParams.set('search', filters.search)
    if (filters.categoryId && filters.categoryId !== 'all') newParams.set('categoryId', filters.categoryId)
    if (filters.ville) newParams.set('ville', filters.ville)
    setSearchParams(newParams)
  }

  const clearFilters = () => {
    setFilters({ search: '', categoryId: '', ville: '' })
    setSearchParams({})
  }

  // Répondre à une demande — Pro uniquement
  const handleRespond = async (e, postId) => {
    e.preventDefault()
    e.stopPropagation()

    if (authLoading) return

    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter')
      navigate('/connexion', { state: { from: '/demandes' } })
      return
    }

    if (!isProView()) {
      toast.error('Passez en mode Professionnel pour répondre aux demandes')
      return
    }

    const authToken = getAuthToken()
    if (!authToken) {
      toast.error('Session expirée. Reconnectez-vous.')
      navigate('/connexion', { state: { from: '/demandes' } })
      return
    }

    try {
      const response = await axios.post(
        `${API}/applications`,
        { postId, message: 'Bonjour, je suis intéressé par votre demande.' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      const chatId = response.data?.data?.chatId ?? response.data?.chatId
      toast.success('Candidature envoyée !')
      if (chatId) {
        navigate(messagingPaths.chat({ chatId, postId }))
      } else {
        navigate('/tableau-de-bord/mes-candidatures')
      }
    } catch (error) {
      const msg = error.response?.data?.error?.message ?? error.response?.data?.message ?? 'Erreur lors de l\'envoi'
      toast.error(msg)
    }
  }

  const activeFiltersCount = Object.values(filters).filter(Boolean).length

  const FilterContent = () => (
    <form className="demandes-filters-form" onSubmit={handleSearch}>
      <div className="demandes-filter-field">
        <label className="demandes-label" htmlFor="request-search">Recherche</label>
        <div className="demandes-filter-search">
          <Search className="w-4 h-4" aria-hidden="true" />
          <input
            id="request-search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Projet ou besoin…"
            data-testid="search-input"
          />
        </div>
      </div>
      <div className="demandes-filter-field">
        <label className="demandes-label">Catégorie</label>
        <Select
          value={filters.categoryId || 'all'}
          onValueChange={(v) => setFilters({ ...filters, categoryId: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="h-12" data-testid="filter-category">
            <SelectValue placeholder="Toutes les catégories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="demandes-filter-field">
        <label className="demandes-label">Ville</label>
        <Input
          placeholder="Ex: Paris"
          value={filters.ville}
          onChange={(e) => setFilters({ ...filters, ville: e.target.value })}
          className="h-12"
          data-testid="filter-city"
        />
      </div>

      <div className="demandes-filter-actions">
        <Button type="submit" className="btn-clickpro-primary" data-testid="apply-filters">
          Afficher les résultats
        </Button>
      </div>
    </form>
  )

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicPageHeader activePath="demandes" />

      <main className="demandes-vitrine flex-1">
        {/* Hero */}
        <section className="demandes-hero">
          <div className="demandes-hero-inner">
            <nav className="demandes-breadcrumb" aria-label="Fil d'Ariane">
              <a href="https://clicketpro.fr">Accueil</a>
              <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
              <span style={{ color: 'var(--dark-blue)', fontWeight: 600 }}>Demandes</span>
            </nav>

            <div className="demandes-eyebrow">Annuaire</div>
            <h1 className="demandes-title">Demandes</h1>
            <p className="demandes-subtitle">Projets en recherche de professionnels qualifiés.</p>
          </div>
        </section>

        {/* Body */}
        <div className="demandes-body">
          <section className="demandes-panel" aria-labelledby="demandes-filters-title">
            <div className="demandes-panel-head">
              <h2 id="demandes-filters-title" className="demandes-panel-title">Affiner les résultats</h2>
              {activeFiltersCount > 0 && (
                <button onClick={clearFilters} className="demandes-panel-clear">
                  Effacer tout
                </button>
              )}
            </div>
            <FilterContent />
            {activeFiltersCount > 0 && (
              <div className="demandes-chips">
                {Object.entries(filters).map(([key, value]) =>
                  value && value !== 'all' ? (
                    <button
                      type="button"
                      key={key}
                      className="demandes-chip"
                      onClick={() => setFilters({ ...filters, [key]: '' })}
                    >
                      {key === 'categoryId'
                        ? (categories.find((c) => c.id === value)?.name ?? value)
                        : value}
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : null
                )}
              </div>
            )}
          </section>

          {/* Results */}
          <div className="min-w-0">
            <div className="demandes-results-head">
              <p className="demandes-count">
                {loading
                  ? 'Chargement…'
                  : accessDenied
                  ? ''
                  : `${totalCount} demande${totalCount > 1 ? 's' : ''} trouvée${totalCount > 1 ? 's' : ''}`}
              </p>
              <Link to="/deposer-demande" className="demandes-btn-primary">
                <FileText className="w-4 h-4" aria-hidden="true" />
                Déposer une demande
              </Link>
            </div>

            {/* Access Denied — not authenticated or not Pro */}
            {!loading && accessDenied && (
              <div className="demandes-state">
                <div className="demandes-state-icon">
                  <Lock className="w-7 h-7" />
                </div>
                <h3 className="demandes-state-title">Accès réservé aux professionnels</h3>
                <p className="demandes-state-text">
                  L&apos;annuaire des demandes est exclusivement accessible aux comptes Pro validés.
                  Connectez-vous ou devenez partenaire pour accéder aux projets.
                </p>
                <div className="flex gap-3 justify-center">
                  {!isAuthenticated ? (
                    <>
                      <Link to="/connexion" className="demandes-btn-outline">Se connecter</Link>
                      <Link to="/inscription" className="demandes-btn-primary">Devenir partenaire</Link>
                    </>
                  ) : (
                    <Link to="/tableau-de-bord/profil" className="demandes-btn-primary">
                      Demander l&apos;accès Pro
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Skeleton loaders */}
            {loading && (
              <div className="demandes-list">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="demandes-skel">
                    <div className="demandes-skel-line" style={{ width: '25%', marginBottom: 16 }} />
                    <div className="demandes-skel-line" style={{ width: '70%', height: 18, marginBottom: 10 }} />
                    <div className="demandes-skel-line" style={{ width: '100%', marginBottom: 16 }} />
                    <div className="demandes-skel-line" style={{ width: '45%' }} />
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && !accessDenied && requests.length === 0 && (
              <div className="demandes-state">
                <div className="demandes-state-icon" style={{ background: 'var(--n100)', color: 'var(--n400)' }}>
                  <Search className="w-7 h-7" />
                </div>
                <h3 className="demandes-state-title">Aucune demande trouvée</h3>
                <p className="demandes-state-text">Essayez de modifier vos critères de recherche.</p>
                <button onClick={clearFilters} className="demandes-btn-outline" style={{ margin: '0 auto' }}>
                  Effacer les filtres
                </button>
              </div>
            )}

            {/* Request cards */}
            {!loading && !accessDenied && requests.length > 0 && (
              <div className="demandes-list">
                {requests.map((request) => {
                  const budget = parseBudget(request.salaryRange)
                  const applicationsCount = request._count?.applications ?? 0

                  return (
                    <Link
                      key={request.id}
                      to={`/demandes/${request.id}`}
                      className="demandes-card"
                      data-testid={`request-card-${request.id}`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-4" style={{ marginBottom: 10 }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          {request.category && (
                            <span
                              className="demandes-cat"
                              style={{
                                backgroundColor: request.category.color
                                  ? `${request.category.color}1A`
                                  : 'var(--primary-50)',
                                color: request.category.color ?? 'var(--primary-dark)',
                              }}
                            >
                              {request.category.name}
                            </span>
                          )}
                          {request.status === 'PUBLISHED' && (
                            <span className="demandes-badge-live">Publié</span>
                          )}
                        </div>
                        <span className="demandes-date">{formatDate(request.createdAt)}</span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="demandes-card-title">{request.title}</h3>
                      <p className="demandes-card-desc line-clamp-2">{request.description}</p>

                      {/* Footer */}
                      <div className="demandes-card-foot">
                        <div className="demandes-meta">
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            {request.locationCity}
                          </span>
                          {applicationsCount > 0 && (
                            <span className="flex items-center gap-1.5 demandes-meta-accent">
                              <Users className="w-4 h-4" />
                              {applicationsCount} réponse{applicationsCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          {budget && (
                            <span className="demandes-budget">
                              <Euro className="w-4 h-4" />
                              {budget.label
                                ? budget.label
                                : budget.min && budget.max
                                ? `${budget.min} – ${budget.max}€`
                                : budget.max
                                ? `Jusqu'à ${budget.max}€`
                                : budget.min
                                ? `À partir de ${budget.min}€`
                                : null}
                            </span>
                          )}

                          {isProView() && (
                            <button
                              className="demandes-respond"
                              onClick={(e) => handleRespond(e, request.id)}
                              data-testid={`respond-btn-${request.id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                              Répondre
                            </button>
                          )}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}
